import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { map, Observable, of, switchMap } from 'rxjs';

import { InvoicesService } from './invoices.service';
import {
  InvoiceDetailModel,
  InvoiceGetDTO,
  InvoiceTypeEnum,
} from './invoice.model';
import { ApiResponseModel } from '../../core/api-response.model';
import { RoleType } from '../../core/services/role.service';
import { UserService } from '../../core/services/user.service';
import { RouteCacheService } from '../../shared/services/route-cache.service';

@Injectable({ providedIn: 'root' })
export class InvoicesResolver
  implements
    Resolve<InvoiceDetailModel | ApiResponseModel<InvoiceGetDTO> | null>
{
  private readonly invoicesService = inject(InvoicesService);
  private readonly userSvc = inject(UserService);
  private readonly routeCache = inject(RouteCacheService);
  private readonly invoiceType = InvoiceTypeEnum;

  resolve(
    route: ActivatedRouteSnapshot,
  ): Observable<InvoiceDetailModel | ApiResponseModel<InvoiceGetDTO> | null> {
    const id = route.paramMap.get('id');
    if (id) {
      return this.invoicesService.getInvoiceById(id);
    }

    const contextRoute = this.routeCache.getContextRoute(route);
    const gridState = contextRoute?.data?.gridInvoiceState;
    const filters = gridState?.filters ?? {};
    const markAsPaidQuery = route.queryParamMap.get('markAsPaid');

    return this.userSvc.user$.pipe(
      switchMap((user) => {
        const page =
          gridState?.paginator?.pageIndex != null
            ? gridState.paginator.pageIndex + 1
            : 1;
        const limit = gridState?.paginator?.pageSize ?? 15;

        if (user?.role.name === RoleType.Admin) {
          const params: Record<string, string | number | boolean> = {
            page,
            limit,
          };

          if (filters.startDate) params['startDate'] = filters.startDate;
          if (filters.endDate) params['endDate'] = filters.endDate;
          if (filters.status) params['status'] = filters.status;
          if (filters.invoiceType) params['invoiceType'] = filters.invoiceType;
          if (filters.clientId) params['clientId'] = filters.clientId;

          const paid =
            markAsPaidQuery != null && markAsPaidQuery !== ''
              ? markAsPaidQuery
              : filters.markAsPaid;
          if (paid === true || paid === 'true') {
            params['markAsPaid'] = true;
          } else if (paid === false || paid === 'false') {
            params['markAsPaid'] = false;
          }

          return this.invoicesService.get(undefined, params).pipe(
            map((response) => ({
              ...response,
              data: response.data.map((item) => ({
                ...item,
                companyName: item.client.companyName,
                clientName: `${item.client.firstName} ${item.client.lastName}`,
              })),
            })),
          );
        }

        if (user?.role.name === RoleType.Client) {
          return this.invoicesService
            .get(undefined, {
              page,
              limit,
              ...(filters.status ? { status: filters.status } : {}),
              clientId: user.clientId,
              invoiceType: this.invoiceType.StaffAugmentation,
            })
            .pipe(
              map((response) => ({
                ...response,
                data: response.data.map((item) => ({
                  ...item,
                  companyName: item.client.companyName,
                  clientName: `${item.client.firstName} ${item.client.lastName}`,
                })),
              })),
            );
        }

        return of(null);
      }),
    );
  }
}
